import { Link } from "wouter";
import { Building, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  TABLE_CARD_CLASS,
  TABLE_GAP_CLASS,
  TABLE_HEAD_CLASS,
  TABLE_ROW_CLASS,
  TableEmpty,
  gridStyle,
} from "@/components/layout/table-chrome";
import { ProjectAvatar } from "@/components/tasks/board-chrome";
import { formatRand } from "@/lib/format-money";
import { projectColorVar } from "@/lib/project-colors";
import { cn } from "@/lib/utils";

/** Same four questions as the Clients page, in the same order, so the two
 *  surfaces don't answer "how is this client doing" differently. */
const GRID = "minmax(0,1fr) 96px 132px 96px";

type ClientWithMetrics = {
  id: string;
  client_uid?: string;
  client_name: string;
  weekly_hours: number;
  income: number;
  hours_logged_this_week?: number;
};

function hoursTone(logged: number, target: number): string {
  if (logged === 0) return "bg-slate-300 dark:bg-slate-600";
  if (target > 0 && logged > target) return "bg-amber-500";
  if (target > 0 && (logged / target) * 100 >= 95) return "bg-emerald-500";
  return "bg-blue-500";
}

export default function ClientList() {
  const { user } = useAuth();
  const { data: clients } = useQuery<ClientWithMetrics[]>({
    queryKey: ["/client_summary/pa/clients", user?.uid],
    enabled: !!user?.uid,
    queryFn: async () => {
      const res = await apiRequest("GET", `/client_summary/pa/${user?.uid}/clients`);
      const data = await res.json();
      return data.clients.map((client: any, index: number) => ({ ...client, id: `client-${index}` })) || [];
    },
  });

  return (
    <section className={TABLE_CARD_CLASS}>
      <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-[18px] py-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-bold tracking-[-0.01em] text-foreground">
            Your clients
          </h2>
          <span className="inline-flex h-5 items-center rounded-full bg-muted px-[7px] text-[11px] font-semibold text-muted-foreground">
            {clients?.length ?? 0}
          </span>
        </div>
        <Link
          href="/clients"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground"
        >
          View all
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div
        className={cn(TABLE_HEAD_CLASS, TABLE_GAP_CLASS)}
        style={gridStyle(GRID)}
      >
        <span>Client</span>
        <span>Plan</span>
        <span>This week</span>
        <span className="text-right">Monthly</span>
      </div>

      {!clients || clients.length === 0 ? (
        <TableEmpty icon={<Building className="h-5 w-5" />} title="No clients yet">
          Clients you&apos;re assigned to will appear here.
        </TableEmpty>
      ) : (
        clients.map((client) => {
          const logged = client.hours_logged_this_week ?? 0;
          const target = client.weekly_hours ?? 0;
          const pct = target > 0 ? Math.min(100, (logged / target) * 100) : 0;
          return (
            <Link
              key={client.id}
              href={`/clients/${client.id}?clientName=${encodeURIComponent(
                client.client_name,
              )}&clientUid=${encodeURIComponent(client.client_uid ?? "")}`}
              className={cn(
                TABLE_ROW_CLASS,
                TABLE_GAP_CLASS,
                "min-[1240px]:h-[42px] cursor-pointer",
              )}
              style={gridStyle(GRID)}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <ProjectAvatar
                  name={client.client_name}
                  color={projectColorVar(client.client_uid || client.client_name)}
                />
                <span className="min-w-0 truncate text-[13px] font-medium tracking-[-0.005em] text-foreground">
                  {client.client_name}
                </span>
              </span>

              <span className="text-[12.5px] tabular-nums text-slate-600 dark:text-slate-300">
                {target.toFixed(1)} hrs/wk
              </span>

              <span className="flex items-center gap-2">
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <span
                    className={cn("block h-full rounded-full", hoursTone(logged, target))}
                    style={{ width: `${pct}%` }}
                  />
                </span>
                <span
                  className={cn(
                    "flex-none whitespace-nowrap text-[11.5px] font-semibold tabular-nums",
                    logged === 0 ? "text-slate-400" : "text-slate-600 dark:text-slate-300",
                  )}
                >
                  {logged.toFixed(1)}
                </span>
              </span>

              <span className="text-right text-[12.5px] font-semibold tabular-nums text-foreground">
                {formatRand(client.income, { cents: false })}
              </span>
            </Link>
          );
        })
      )}
    </section>
  );
}
